import { useEffect, useState, Fragment, useMemo } from 'react';
import clsx from 'clsx';

import { renderWithIndex } from 'src/tools/render.tools';
import { ButtonBaseType, ID, Maybe } from 'src/models';
import { ifTrue } from 'src/tools/logic.tools';
import { useKeyPress } from 'src/hooks';
import { Item } from './Item';

import classes from './TreeMenu.module.scss';

export interface Props<T> {
  className?: string;
  currentItemId?: ID;
  items?: T[];
  onItemClick: (id: ID) => void;
  onEnterPressed?: (e: KeyboardEvent) => void;
  renderItemLabel?: (item: T, isSelected: boolean, isActive: boolean) => string;
  isSmall?: boolean;
  isDisableNavigation?: boolean;
}

export function TreeMenu<T extends ButtonBaseType = ButtonBaseType>({
  className,
  items = [],
  onItemClick,
  isSmall,
  currentItemId,
  renderItemLabel,
  isDisableNavigation,
  onEnterPressed,
}: Props<T>) {
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const selectedItem = useMemo(() => {
    if (selectedIndex === -1) return;

    let currIndex = 0;
    let result: Maybe<ButtonBaseType>;

    function processItem(i: ButtonBaseType) {
      if (result) return;

      if (currIndex === selectedIndex) {
        result = i;

        return;
      } else {
        currIndex += 1;

        i.items?.forEach(processItem);
      }
    }

    items.forEach(processItem);

    return result;
  }, [items, selectedIndex]);

  function handleOnKeyDown(e: KeyboardEvent) {
    if (['ArrowUp', 'ArrowDown'].indexOf(e.code) > -1) e.preventDefault();
  }

  // prevent window scrolling when navigation is enabled
  useEffect(() => {
    if (!!items.length && !isDisableNavigation) {
      window.addEventListener('keydown', handleOnKeyDown, false);

      return () => window.removeEventListener('keydown', handleOnKeyDown);
    }
  }, [isDisableNavigation, items.length]);

  useKeyPress(
    {
      onEnter: handleClickSelected,
      onArrowUp: handleArrowUp,
      onArrowDown: handleArrowDown,
    },
    !!items.length && !isDisableNavigation,
  );

  const notDisabledIndexes: ID[] = [];

  function handleArrowDown(e: KeyboardEvent) {
    if (!notDisabledIndexes.length) return;

    e.preventDefault();

    const lastIndex = notDisabledIndexes[notDisabledIndexes.length - 1];

    let nextIndex = selectedIndex;

    do {
      nextIndex += 1;

      if (nextIndex === lastIndex) nextIndex = 0;
    } while (nextIndex !== selectedIndex && !notDisabledIndexes.includes(nextIndex));

    if (nextIndex !== selectedIndex) setSelectedIndex(nextIndex);
  }

  function handleArrowUp(e: KeyboardEvent) {
    if (!notDisabledIndexes.length) return;

    e.preventDefault();

    let prevIndex = selectedIndex;

    do {
      prevIndex -= 1;

      if (prevIndex < notDisabledIndexes[0]) {
        prevIndex = items.length - 1;
      }
    } while (prevIndex !== selectedIndex && !notDisabledIndexes.includes(prevIndex));

    if (prevIndex !== selectedIndex) setSelectedIndex(prevIndex);
  }

  function getItemHandleClick(id: ID) {
    return function call() {
      onItemClick(id);
    };
  }

  function getItemHandleMouseEnter(index: number, isDisabled?: boolean) {
    if (!isDisabled) {
      return function call() {
        setSelectedIndex(index);
      };
    }
  }

  function handleOnMouseLeave() {
    if (isDisableNavigation) setSelectedIndex(-1);
  }

  function handleClickSelected(e: KeyboardEvent) {
    e.preventDefault();

    if (selectedItem) {
      onItemClick(selectedItem.id);
    } else if (onEnterPressed) onEnterPressed(e);
  }

  function renderSubMenu(item: T, index: number, depth: number) {
    if (!item.items?.length) return <></>;

    return renderWithIndex(item.items as T[], getRenderItem(index + 1, depth + 1, item.isChecked));
  }

  function getRenderItem(startIndex: number, depth: number, isCheckedParent?: boolean) {
    return function renderItem(item: T, currentIndex: number) {
      const { id, label, isDisabled, isSplitAfter, items: subItems, isChecked: isCheckedSelf, ...rest } = item;

      const index = startIndex + currentIndex;

      const isSelected = index === selectedIndex;

      const isActive = id === currentItemId;

      const isItemChecked = (i: ButtonBaseType) => i.isChecked;

      const isItemNotChecked = (i: ButtonBaseType) => !i.isChecked;

      const isChecked = isCheckedParent || isCheckedSelf;

      const isIndeterminate = !isChecked && subItems?.some(isItemChecked) && subItems?.some(isItemNotChecked);

      if (!isDisabled) notDisabledIndexes.push(index);

      return (
        <Fragment key={id}>
          <Item
            {...rest}
            id={id}
            label={renderItemLabel ? renderItemLabel(item, isSelected, isActive) : label}
            onClick={getItemHandleClick(id)}
            onMouseEnter={getItemHandleMouseEnter(index, isDisabled)}
            isSelected={isSelected}
            isActive={isActive}
            isDisabled={isDisabled}
            isSmall={isSmall}
            isSplitAfter={isSplitAfter}
            isChecked={isChecked}
            isIndeterminate={isIndeterminate}
            depth={depth}
          />
          {ifTrue(subItems?.length, renderSubMenu(item, depth, index))}
        </Fragment>
      );
    };
  }

  if (!items.length) return <></>;

  return (
    <div
      data-testid='tree-menu'
      className={clsx(classes.container, 'column', className)}
      onMouseLeave={handleOnMouseLeave}
    >
      {renderWithIndex(items, getRenderItem(0, 0))}
    </div>
  );
}
